import React, { useState, useMemo, useEffect } from 'react';
import { ErrorMessage, ZipField, Label } from '../FormikCustom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import qs from 'qs';
import './PaymentApplication.scss';
import { api } from '../../api/client';
import apiHelper from '../../api/apiHelper';
import { Alert, ProgressBar } from 'react-bootstrap';
import useTokenAuthentication from '../../hooks/useTokenAuthentication';
import { STEP_STATUS, AUTH_ERROR_TYPES } from '../../constants/authConstants';
import {
  InvalidTokenScreen,
  ExpiredTokenScreen,
  MissingTokenScreen,
} from '../Auth/AuthErrorScreens';
import { serverLogger } from '../../libs/datadog-logger/loggers/serverLogger';
import { appendCidToUrl, getCorrelationId } from '../../libs/correlationId';
import { DD_MOP_EVENTS } from '../../auth/datadogEvents';
import {
  MOP_AUTHORIZATION_FAILURE_TYPES,
  classifyMopAuthorizationFailure,
  getMopAuthorizationUserMessage,
} from './mopAuthorizationErrorHandling';

export default function MethodOfPaymentAuthorization({ location, history }) {
  const [serverResponse, setServerResponse] = useState(null);
  const [serverError, setServerError] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { app } = params;

  // Use authentication hook
  const {
    authErrorType,
    authStepOneStatus,
    userName,
    isAuthenticating,
    loadProgress,
    loadStep,
    getAuthToken,
  } = useTokenAuthentication({ key: params.key, token: params.token });

  const correlationId = useMemo(() => getCorrelationId(app, params.cid), [app, params.cid]);
  const mopAuthLogger = useMemo(
    () => serverLogger.child({ correlationId, appNumber: app, component: 'MethodOfPaymentAuthorization' }),
    [correlationId, app],
  );

  const isUiDisabled = isAuthenticating || loading || !userName;

  // Log component mount (entry point)
  useEffect(() => {
    mopAuthLogger.info(DD_MOP_EVENTS.PAGE_VIEW, {
      appNumber: app,
      flowStage: DD_MOP_EVENTS.PAGE_VIEW,
      message: 'MOP Auth: Page view',
    });
  }, [app, mopAuthLogger]);

  // Log authentication status changes
  useEffect(() => {
    if (authStepOneStatus === STEP_STATUS.success && userName) {
      mopAuthLogger.info(DD_MOP_EVENTS.AUTH_SUCCESS, {
        userName,
        appNumber: app,
        flowStage: DD_MOP_EVENTS.AUTH_SUCCESS,
        message: 'MOP Auth: Authentication successful',
      });
    }
  }, [authStepOneStatus, userName, app, correlationId, mopAuthLogger]);

  // Log authentication failures with detailed context
  useEffect(() => {
    if (authStepOneStatus === STEP_STATUS.failed) {
      const errorDetails = {
        appNumber: app,
        errorType: authErrorType,
        correlationId,
      };

      if (authErrorType === AUTH_ERROR_TYPES.EXPIRED_TOKEN) {
        mopAuthLogger.error(DD_MOP_EVENTS.ERROR_SCREEN_EXPIRED, {
          ...errorDetails,
          flowStage: DD_MOP_EVENTS.ERROR_SCREEN_EXPIRED,
          message: 'MOP Auth: Token expired - showing expired screen',
        });
      } else if (authErrorType === AUTH_ERROR_TYPES.INVALID_TOKEN) {
        mopAuthLogger.error(DD_MOP_EVENTS.ERROR_SCREEN_INVALID, {
          ...errorDetails,
          flowStage: DD_MOP_EVENTS.ERROR_SCREEN_INVALID,
          message: 'MOP Auth: Invalid token - showing invalid screen',
        });
      } else if (authErrorType === AUTH_ERROR_TYPES.MISSING_TOKEN) {
        mopAuthLogger.warn(DD_MOP_EVENTS.ERROR_SCREEN_MISSING, {
          ...errorDetails,
          flowStage: DD_MOP_EVENTS.ERROR_SCREEN_MISSING,
          message: 'MOP Auth: Missing token - showing missing screen',
        });
      }
    }
  }, [authStepOneStatus, authErrorType, app, correlationId, mopAuthLogger]);

  // Validation schema for ZIP code
  const zipValidationSchema = Yup.object().shape({
    zip: Yup.string().label('Business ZIP').zip().required(),
  });

  const onSubmitHandler = async (values, { setSubmitting }) => {
    setLoading(true);
    setServerError(null);

    // Log form submission start
    mopAuthLogger.info(DD_MOP_EVENTS.ZIP_SUBMIT_START, {
      userName,
      appNumber: app,
      flowStage: DD_MOP_EVENTS.ZIP_SUBMIT_START,
      hasZip: !!values?.zip,
      message: 'MOP Auth: Form submission started',
    });

    try {
      if (!values?.zip) {
        mopAuthLogger.debug(DD_MOP_EVENTS.ZIP_VALIDATION_FAILED, {
          userName,
          appNumber: app,
          flowStage: DD_MOP_EVENTS.ZIP_VALIDATION_FAILED,
          reason: 'empty_zip',
          message: 'MOP Auth: Validation - empty ZIP (user input)',
        });
        setServerError('Please enter a valid ZIP code.');
        return;
      }

      // Get token from hook
      const token = getAuthToken();
      if (!token) {
        mopAuthLogger.warn(DD_MOP_EVENTS.ZIP_VALIDATION_FAILED, {
          userName,
          appNumber: app,
          flowStage: DD_MOP_EVENTS.ZIP_VALIDATION_FAILED,
          reason: 'missing_token',
          message: 'MOP Auth: No token available for submission',
        });
        setServerError(
          getMopAuthorizationUserMessage(MOP_AUTHORIZATION_FAILURE_TYPES.AUTH_ERROR, ''),
        );
        return;
      }

      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;

      mopAuthLogger.debug(DD_MOP_EVENTS.ZIP_API_START, {
        userName,
        appNumber: app,
        flowStage: DD_MOP_EVENTS.ZIP_API_START,
        message: 'MOP Auth: ZIP verification API call started',
      });

      const response = await api.post(
        `payments/${app}/method-of-payment-authorization`,
        {
          applicationNumber: app,
          userName,
          zipCode: values.zip,
        },
        config,
      );

      const responseData = response.data;
      setServerResponse(responseData);

      if (responseData && responseData.IsAuthorized) {
        mopAuthLogger.info(DD_MOP_EVENTS.ZIP_SUCCESS, {
          userName,
          appNumber: app,
          isAuthorized: responseData.IsAuthorized,
          hasRedirectLink: !!responseData.Link,
          flowStage: DD_MOP_EVENTS.ZIP_SUCCESS,
          message: 'MOP Auth: ZIP verification successful - authorized',
        });

        setServerResponse('');
        if (responseData.Link && responseData.Link.startsWith('http')) {
          const url = responseData.Link + (responseData.Link.includes('?') ? '&' : '?') + 'from=mop-auth';
          const urlWithCid = appendCidToUrl(url, correlationId);

          mopAuthLogger.info(DD_MOP_EVENTS.REDIRECT_EXTERNAL, {
            userName,
            appNumber: app,
            flowStage: DD_MOP_EVENTS.REDIRECT_EXTERNAL,
            targetUrl: urlWithCid.split('?')[0], // Log URL without query params
            message: 'MOP Auth: Redirecting to external URL',
          });

          window.location.href = urlWithCid;
        } else if (responseData.Link) {
          const basePath = responseData.Link.startsWith('/') ? responseData.Link : `/${responseData.Link}`;
          const separator = basePath.includes('?') ? '&' : '?';
          const path = `${basePath}${separator}from=mop-auth`;
          const pathWithCid = appendCidToUrl(path, correlationId);

          mopAuthLogger.info(DD_MOP_EVENTS.REDIRECT_INTERNAL, {
            userName,
            appNumber: app,
            flowStage: DD_MOP_EVENTS.REDIRECT_INTERNAL,
            targetPath: basePath,
            message: 'MOP Auth: Redirecting to internal path',
          });

          history.push(pathWithCid);
        } else {
          mopAuthLogger.warn(DD_MOP_EVENTS.ZIP_ERROR, {
            userName,
            appNumber: app,
            flowStage: DD_MOP_EVENTS.ZIP_ERROR,
            redirectResult: 'missing_link',
            message: 'MOP Auth: Authorized but no redirect link provided',
          });
        }
      } else {
        const failureMessage = responseData?.message || 'Failed to verify ZIP code. Please try again.';
        mopAuthLogger.info(DD_MOP_EVENTS.ZIP_DENIED, {
          userName,
          appNumber: app,
          flowStage: DD_MOP_EVENTS.ZIP_DENIED,
          serverMessage: failureMessage,
          message: 'MOP Auth: ZIP verification - not authorized (normal case)',
        });
        setServerError(failureMessage);
      }
    } catch (error) {
      const msg = apiHelper.parseError(error);
      const statusCode = error?.response?.status;
      const isNetworkError = !error?.response;
      const is401 = statusCode === 401;
      const failureType = classifyMopAuthorizationFailure(error);

      mopAuthLogger.error(DD_MOP_EVENTS.ZIP_ERROR, {
        userName,
        appNumber: app,
        error: msg,
        statusCode,
        isNetworkError,
        is401,
        failureType,
        flowStage: DD_MOP_EVENTS.ZIP_ERROR,
        message: 'MOP Auth: ZIP verification API error',
      });

      setServerError(getMopAuthorizationUserMessage(failureType, msg));
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  // Show error screens if authentication fails
  if (authStepOneStatus === STEP_STATUS.failed && authErrorType === AUTH_ERROR_TYPES.INVALID_TOKEN) {
    return <InvalidTokenScreen />;
  }

  if (authStepOneStatus === STEP_STATUS.failed && authErrorType === AUTH_ERROR_TYPES.EXPIRED_TOKEN) {
    return <ExpiredTokenScreen />;
  }

  if (authStepOneStatus === STEP_STATUS.failed && authErrorType === AUTH_ERROR_TYPES.MISSING_TOKEN) {
    return <MissingTokenScreen />;
  }

  return (
    <div className="mop-authorization-container">
      {serverError ? (
        <Alert variant="danger" onClose={() => setServerError(null)} dismissible={true}>
          {serverError}
        </Alert>
      ) : null}
      {(isAuthenticating || loading) && (
        <div style={{ maxWidth: 420, margin: '0 0 1rem 0' }}>
          <ProgressBar now={loadProgress} label={loadStep} animated striped />
        </div>
      )}
      <div className="mop-header" style={{ opacity: isUiDisabled ? 0.6 : 1 }}>
        <h1 className="mop-h1">Method Of Payment Authorization</h1>
        <h2 className="mop-h2">Application #: {app}</h2>
        <p>
          The following payment information will be used for the payment that is due at signing and/or your ongoing
          payments, depending on the requirements of your contract.
        </p>
        <p>To verify your identity, please enter the Business ZIP Code you provided on your application.</p>
      </div>
      <div style={{ marginTop: '20px', pointerEvents: isUiDisabled ? 'none' : 'auto' }}>
        <Formik initialValues={{ zip: '' }} validationSchema={zipValidationSchema} onSubmit={onSubmitHandler}>
          {({ isSubmitting, errors, touched }) => (
            <Form className="mop-authorization-form">
              <div className="form-group">
                <div className="form-row">
                  <Label htmlFor="zip" className="form-label">
                    Verify Business ZIP Code
                  </Label>
                  <div className="form-field">
                    <ZipField
                      className={`form-control ${touched.zip && errors.zip ? 'is-invalid' : ''}`}
                      name="zip"
                      placeholder="Enter ZIP code"
                      disabled={isUiDisabled}
                    />
                  </div>
                </div>
                <ErrorMessage name="zip" component="div" className="text-danger" />
              </div>

              <div className="form-group mt-4">
                <button type="submit" className="btn btn-primary" disabled={isUiDisabled || isSubmitting || loading}>
                  {isSubmitting || loading ? 'Verifying...' : 'Submit'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>{' '}
      {serverResponse && <div className="alert alert-success mt-3">{serverResponse}</div>}
    </div>
  );
}
